import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

// Icône selon le type de notification (module Caisse & Rapprochement).
const TYPE_ICON = {
  topup_request: "Wallet",
  topup_approved: "CheckCircle2",
  topup_rejected: "XCircle",
  cash_expense: "Receipt",
  reconciliation: "AlertTriangle",
  forgot_pin: "KeyRound",
  forgot_identifier: "HelpCircle",
  role_request: "ShieldAlert",
  role_approved: "ShieldCheck",
  role_rejected: "ShieldX",
};

// Où renvoie un clic sur la notification.
function routeFor(n) {
  if (n.type === "ticket_new" || n.ref_type === "ticket") return "/tickets";
  if (n.ref_type === "topup" || n.type === "cash_expense") return "/finances?tab=cash_box";
  if (n.type === "reconciliation" || n.ref_type === "bank_statement") return "/finances?tab=bank_account";
  if (["forgot_pin", "forgot_identifier", "role_request", "role_approved", "role_rejected"].includes(n.type)
      || n.ref_type === "user" || n.ref_type === "role_request") return "/users";
  return "/";
}

// Hook unique (une seule source de vérité + un seul polling) partagé par la cloche et le bandeau.
export function useNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { console.debug("notifications: chargement différé (nouvel essai dans 25 s)", e?.message); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 25000);
    return () => clearInterval(id);
  }, [load]);

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.put(`/notifications/${id}/read`); } catch (e) { console.warn("notifications: échec marquage lu (UI déjà à jour)", e?.message); }
  }, []);

  const markAll = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try { await api.put("/notifications/read-all"); } catch (e) { console.warn("notifications: échec 'tout marquer lu' (UI déjà à jour)", e?.message); }
  }, []);

  return { items, unread, markRead, markAll };
}

// Cloche 🔔 dans l'en-tête + panneau déroulant.
export function NotificationBell({ notif }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const { items, unread, markRead, markAll } = notif;
  const [open, setOpen] = useState(false);

  const openItem = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    navigate(routeFor(n));
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell"
        className="relative h-10 w-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center active:scale-95 transition-transform">
        <Icons.Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge"
            className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="notif-backdrop" />
          <div data-testid="notif-panel"
            className="absolute end-0 mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto rounded-2xl bg-card border border-border shadow-xl z-50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-card">
              <p className="font-bold">{t("notifications")}</p>
              {unread > 0 && (
                <button onClick={markAll} data-testid="notif-mark-all"
                  className="text-xs font-semibold text-brand dark:text-primary">{t("mark_all_read")}</button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="notif-empty">{t("no_notifications")}</div>
            ) : (
              <div className="divide-y divide-border">
                {items.map((n) => {
                  const Ico = Icons[TYPE_ICON[n.type]] || Icons.Bell;
                  return (
                    <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
                      className={`w-full text-start px-4 py-3 flex gap-3 items-start transition-colors ${!n.read ? "bg-accent/60" : "hover:bg-accent"}`}>
                      <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${!n.read ? "bg-terracotta/15 text-terracotta" : "bg-secondary text-muted-foreground"}`}>
                        <Ico className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-sm">{n.title}</span>
                        {n.body && <span className="block text-xs text-muted-foreground mt-0.5">{n.body}</span>}
                      </span>
                      {!n.read && <span className="h-2 w-2 rounded-full bg-terracotta shrink-0 mt-1.5" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Bandeau rouge (une notification non lue à la fois) — fermable une fois lu.
export function NotificationBanner({ notif }) {
  const navigate = useNavigate();
  const { items, markRead } = notif;
  const unreadItems = items.filter((n) => !n.read);
  const n = unreadItems[0];
  if (!n) return null;
  const Ico = Icons[TYPE_ICON[n.type]] || Icons.Bell;

  return (
    <div data-testid="notif-banner" className="bg-terracotta text-white">
      <div className="mx-auto max-w-3xl px-4 py-2.5 flex items-center gap-3">
        <Ico className="h-5 w-5 shrink-0" />
        <button onClick={() => navigate(routeFor(n))} data-testid="notif-banner-body" className="min-w-0 flex-1 text-start">
          <p className="font-bold text-sm truncate">{n.title}</p>
          {n.body && <p className="text-xs text-white/85 truncate">{n.body}</p>}
        </button>
        {unreadItems.length > 1 && (
          <span className="text-[11px] font-bold bg-white/20 rounded-full px-2 py-0.5 shrink-0">+{unreadItems.length - 1}</span>
        )}
        <button onClick={() => markRead(n.id)} data-testid="notif-banner-close"
          className="h-7 w-7 rounded-full hover:bg-white/20 flex items-center justify-center shrink-0">
          <Icons.X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
